import Loading from "components/Loading";
import React, { useState, useEffect } from "react";
import {
  BRAND_GREEN_PRIMARY,
  BRAND_GREEN_VIBRANT,
} from "resources/constants/colors";
import useUser from "state/user";
import styled, { keyframes } from "styled-components";
import Therapist from "./Therapist";
import Client from "./Client";
import SOSButton from "./components/SOSButton";
import { UserTypes } from "resources/constants/config";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useHistory } from "react-router-dom";
import { IMAGES_URL } from "resources/constants/urls";
import NoProfileSVG from "resources/img/no-profile.svg";
import MenuSVG from "resources/img/menu.svg";
import { notificationsAPI } from "resources/api";

// ---------------------------------------------------------------------------
// Animations
// ---------------------------------------------------------------------------

const fadeDown = keyframes`
  from { opacity: 0; transform: translateY(-8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

const Header = styled.div`
  background: linear-gradient(
    140deg,
    ${BRAND_GREEN_PRIMARY} 0%,
    ${BRAND_GREEN_VIBRANT} 100%
  );
  border-radius: 0 0 28px 28px;
  padding: 20px 20px 28px;
  margin: -16px -16px 0;
  position: relative;
  animation: ${fadeDown} 0.45s ease both;
`;

// ---------------------------------------------------------------------------
// Header subcomponents (redesign 26G)
// ---------------------------------------------------------------------------

const HeaderTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MenuBtn = styled.button`
  background: rgba(255, 255, 255, 0.25);
  border: none;
  border-radius: 50%;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  -webkit-tap-highlight-color: transparent;
  img { width: 22px; height: 22px; opacity: 0.85; }
  &:active { opacity: 0.6; }
`;

const NotifButton = styled.button`
  background: rgba(255, 255, 255, 0.25);
  border: none;
  border-radius: 50%;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  position: relative;
  -webkit-tap-highlight-color: transparent;
  &:active { opacity: 0.6; }
`;

const NotifBadge = styled.span`
  position: absolute;
  top: 2px;
  right: 2px;
  background: #f44336;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  min-width: 16px;
  height: 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  line-height: 1;
`;

const AvatarRing = styled.button`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 2.5px solid rgba(255, 255, 255, 0.85);
  overflow: hidden;
  background: rgba(255, 255, 255, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  font-weight: 800;
  color: #2a3d00;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
  &:active { opacity: 0.75; }
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const DateLabel = styled.p`
  font-size: 13px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.55);
  margin: 0 0 12px;
  text-transform: capitalize;
`;

const GreetingTitle = styled.h1`
  font-size: 26px;
  font-weight: 800;
  color: #1a2700;
  margin: 0 0 2px;
  line-height: 1.2;
`;

const GreetingSubtitle = styled.p`
  font-size: 14px;
  color: rgba(0, 0, 0, 0.55);
  margin: 0;
  font-weight: 500;
`;

// ---------------------------------------------------------------------------
// Content wrapper
// ---------------------------------------------------------------------------

// PageRoot reemplaza Scrollable: el scroll ocurre en el window (como Calendar y
// Messages), no dentro de un contenedor de altura fija.
const PageRoot = styled.div`
  padding: 16px 0 0;
  padding-bottom: 100px;
`;

const ContentPad = styled.div`
  padding-top: 0;
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Buenos días";
  if (hour < 19) return "Buenas tardes";
  return "Buenas noches";
};

const getInitials = (name = "") =>
  name
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase() ?? "")
    .join("");

// ---------------------------------------------------------------------------
// Fallback: leer userType del token guardado al hacer login
// Útil cuando /profile retorna 401 (token expirado) pero el usuario ya estaba
// en sesión — evita spinner eterno.
// ---------------------------------------------------------------------------

const getStoredUserType = () => {
  try {
    const stored = JSON.parse(localStorage.getItem("auth") || "{}");
    return stored?.userType ?? null;
  } catch {
    return null;
  }
};

// ---------------------------------------------------------------------------
// Summary page
// ---------------------------------------------------------------------------

const Summary = ({ toggleMenu }) => {
  const [user] = useUser();
  const history = useHistory();
  const [unreadCount, setUnreadCount] = useState(0);

  const isFetching = user?.fetching?.fetch?.state === true;

  // userType: lo que devuelve /profile (fuente confiable) —
  // o bien, lo que se guardó en localStorage al hacer login (fallback cuando el
  // fetch aún corre o falló con 401 pero el JWT sigue válido para la ruta).
  const userType =
    user?.current?.userType ??
    (!isFetching ? getStoredUserType() : null);

  const name = user?.current?.name ?? "";
  const profileImg = user?.current?.profileImg;
  const dateStr = format(new Date(), "EEEE, d 'de' MMMM", { locale: es });

  useEffect(() => {
    notificationsAPI.unreadCount()
      .then((data) => setUnreadCount(data?.unreadCount || 0))
      .catch(() => {});
  }, []);

  return (
    <>
      {userType !== UserTypes.THERAPIST && (
        <SOSButton onNavigate={(path) => history.push(path)} />
      )}
      <PageRoot>
        {/* Gradient header — redesign 26G */}
        <Header>
          {/* Fila 1: hamburger | campanita + avatar */}
          <HeaderTopRow>
            <MenuBtn onClick={toggleMenu} aria-label="Menú">
              <img src={MenuSVG} alt="Menú" />
            </MenuBtn>
            <HeaderActions>
              <NotifButton onClick={() => history.push('/notificaciones')} aria-label="Notificaciones">
                🔔
                {unreadCount > 0 && <NotifBadge>{unreadCount > 99 ? '99+' : unreadCount}</NotifBadge>}
              </NotifButton>
              <AvatarRing
                onClick={() => history.push('/perfil')}
                aria-label="Ver perfil"
              >
                {profileImg ? (
                  <img src={`${IMAGES_URL}${profileImg}`} alt="Perfil" />
                ) : name ? (
                  getInitials(name)
                ) : (
                  <img src={NoProfileSVG} alt="Perfil" />
                )}
              </AvatarRing>
            </HeaderActions>
          </HeaderTopRow>

          {/* Fila 2: fecha */}
          <DateLabel>{dateStr}</DateLabel>

          {/* Fila 3: saludo */}
          <GreetingTitle>
            {getGreeting()}{name ? `, ${name}` : ""} 👋
          </GreetingTitle>
          <GreetingSubtitle>¿Cómo te sientes hoy?</GreetingSubtitle>
        </Header>

        {/* Main content */}
        <ContentPad>
          {isFetching ? (
            <Loading />
          ) : userType === UserTypes.THERAPIST ? (
            <Therapist />
          ) : (
            // CLIENT es el caso default; también cubre cuando userType = 'client'
            // o cuando venimos del fallback de localStorage.
            <Client />
          )}
        </ContentPad>
      </PageRoot>
    </>
  );
};

export default Summary;
